// src/beta/components/AxisSwitcher.tsx
import { KeyboardEvent } from 'react';
import type { Axis } from '@/beta/types/showcase';

const AXES: { value: Axis; label: string }[] = [
  { value: 'phase', label: 'Phase' },
  { value: 'component', label: 'Component' },
  { value: 'provenance', label: 'Provenance' },
  { value: 'documents', label: 'Documents' },
  { value: 'verification', label: 'Verification' },
];

interface AxisSwitcherProps {
  value: Axis;
  onChange: (axis: Axis) => void;
}

/** Segmented control for the 5 Focus axes. Click changes active axis;
 *  arrow keys navigate left/right with wraparound. Active button is
 *  red on dark; inactive buttons are stone with hover bump.
 *  Sharp corners — radius 0 across the beta tree.
 *
 *  Accessibility: role="tablist" on wrapper, role="tab" on each slot,
 *  type="button" inner element carries aria-pressed and accessible name. */
export function AxisSwitcher({ value, onChange }: AxisSwitcherProps) {
  const idx = AXES.findIndex((a) => a.value === value);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const delta = e.key === 'ArrowRight' ? 1 : -1;
    const next = AXES[(idx + delta + AXES.length) % AXES.length];
    onChange(next.value);
  };

  const handleClick = (axis: Axis) => {
    if (axis === value) return;
    onChange(axis);
  };

  return (
    <div
      role="tablist"
      aria-label="Showcase axis"
      onKeyDown={handleKeyDown}
      style={{
        display: 'flex',
        gap: 0,
        border: '1px solid var(--vm-border)',
        listStyle: 'none',
        margin: 0,
        padding: 0,
      }}
    >
      {AXES.map((axis) => {
        const active = axis.value === value;
        return (
          <div
            key={axis.value}
            role="tab"
            aria-selected={active}
            style={{ display: 'contents' }}
          >
            <button
              type="button"
              aria-pressed={active}
              tabIndex={active ? 0 : -1}
              onClick={() => handleClick(axis.value)}
              style={{
                padding: '12px 18px',
                fontFamily: 'var(--vm-font-heading)',
                fontSize: 11,
                letterSpacing: '0.3em',
                textTransform: 'uppercase',
                background: active ? 'var(--vm-red)' : 'transparent',
                color: active ? 'var(--vm-warm-white)' : 'var(--vm-stone)',
                border: 'none',
                borderRight: '1px solid var(--vm-border)',
                cursor: active ? 'default' : 'pointer',
                transition: 'background 250ms ease, color 250ms ease',
              }}
            >
              {axis.label}
            </button>
          </div>
        );
      })}
    </div>
  );
}
